/**
 * Slice для управления состоянием языка в Redux
 * 
 * Отвечает за:
 * - Хранение текущего языка
 * - Изменение языка приложения
 * - Определение языка из email адреса
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { setCookie, getCookie } from '../../utils/cookieUtils';
import { AppThunk } from '../store';

interface LanguageState {
    currentLanguage: string;
}

// Начальное состояние - получаем язык из cookie или localStorage
const initialState: LanguageState = {
    currentLanguage: getCookie('language') || localStorage.getItem('i18nextLng') || 'ru'
};

const languageSlice = createSlice({
    name: 'language',
    initialState,
    reducers: {
        setLanguage: (state, action: PayloadAction<string>) => {
            state.currentLanguage = action.payload;
        }
    }
});

// Экспорт actions
export const { setLanguage } = languageSlice.actions;

/**
 * Асинхронный action для изменения языка с сохранением в cookie и localStorage
 */
export const changeLanguage = (language: string): AppThunk => (dispatch) => {
    // Меняем язык в i18n
    i18n.changeLanguage(language);

    // Сохраняем в localStorage
    localStorage.setItem('i18nextLng', language);

    // Сохраняем в cookie на 30 дней
    setCookie('language', language, 30);

    // Обновляем состояние в Redux
    dispatch(setLanguage(language));
};

/**
 * Функция для определения языка из email адреса
 */
export const detectLanguageFromEmail = (email: string): string => {
    // По умолчанию используем русский язык
    let detectedLanguage = 'ru';

    // Проверяем наличие метки языка в email (_ru, _it, и т.д.)
    const languageMatch = email.match(/_([a-z]{2})@/i);
    if (languageMatch && languageMatch[1]) {
        // Преобразуем в нижний регистр для единообразия
        const langCode = languageMatch[1].toLowerCase();

        // Принимаем только поддерживаемые языки (ru, it)
        if (['ru', 'it'].includes(langCode)) {
            detectedLanguage = langCode;
        }
    }

    return detectedLanguage;
};

export default languageSlice.reducer; 